import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  AlertTriangle,
  CheckCircle,
  ExternalLink,
  FileText,
  Loader2,
  Settings,
} from 'lucide-react';
import API_BASE from '../../utils/apiBase';
import { extractTenantSetupGate } from '../../utils/tenantSetupGate';
import TenantSetupGateNotice from './TenantSetupGateNotice';

const API = API_BASE;

/**
 * EmploymentPackPanel — admin view for preparing contract + handbook PDFs
 * with no unresolved placeholders before the worker signs.
 */
export default function EmploymentPackPanel({
  employeeId,
  onRefresh,
  isAuditor = false,
}) {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [preparing, setPreparing] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [setupGate, setSetupGate] = useState(null);

  const fetchReadiness = useCallback(async () => {
    if (!employeeId || !token) return;
    setLoading(true);
    setError(null);
    setSetupGate(null);
    try {
      const res = await axios.get(
        `${API}/employees/${employeeId}/employment-pack/readiness`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to load employment pack status');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [employeeId, token]);

  useEffect(() => {
    fetchReadiness();
  }, [fetchReadiness]);

  const handlePrepare = async () => {
    if (!employeeId || !token || isAuditor) return;
    setPreparing(true);
    setError(null);
    setSetupGate(null);
    try {
      await axios.post(
        `${API}/employees/${employeeId}/employment-pack/prepare`,
        { apply_org_defaults: true, render: true },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      await fetchReadiness();
      if (onRefresh) onRefresh();
    } catch (err) {
      const gate = extractTenantSetupGate(err);
      if (gate) {
        setSetupGate(gate);
        setError(null);
        return;
      }
      const detail = err.response?.data?.detail;
      if (typeof detail === 'object' && detail?.readiness) {
        setData((prev) => ({
          ...(prev || {}),
          readiness: detail.readiness,
        }));
        setError(detail.message || 'Employment pack could not be prepared');
      } else {
        setError(
          typeof detail === 'string'
            ? detail
            : detail?.message || err.message || 'Failed to prepare employment pack',
        );
      }
    } finally {
      setPreparing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 p-4 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-600">
        <Loader2 className="h-4 w-4 animate-spin" />
        Checking employment pack readiness…
      </div>
    );
  }

  const readiness = data?.readiness;
  const agreements = data?.agreements || {};
  const isReady = readiness?.ready;
  const contractBlockers = readiness?.contract?.blockers || [];
  const handbookBlockers = readiness?.handbook?.blockers || [];
  const contentGaps = readiness?.handbook?.content_gaps || [];

  return (
    <div
      className="p-5 bg-white border border-slate-200 rounded-xl shadow-sm"
      data-testid="employment-pack-panel"
    >
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h3 className="font-heading font-semibold text-text-primary flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary" />
            Employment Pack
          </h3>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            Prepare the contract and handbook PDFs with all required fields filled in —
            no TBC values or template placeholders should appear in what the worker sees.
          </p>
        </div>
        <Badge
          variant={isReady ? 'default' : 'destructive'}
          className={isReady ? 'bg-green-600 hover:bg-green-600' : ''}
        >
          {isReady ? 'Ready to render' : 'Action required'}
        </Badge>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700 flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {setupGate && (
        <div className="mb-4">
          <TenantSetupGateNotice
            gate={setupGate}
            title="Workspace setup must be complete before preparing this pack"
          />
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-2 mb-4">
        <PackStatusCard
          title="Employment Contract"
          ready={readiness?.contract?.ready}
          blockers={contractBlockers}
          renderedUrl={agreements.contract?.rendered_file_url}
          renderIssue={agreements.contract?.render_issue}
        />
        <PackStatusCard
          title="Employee Handbook"
          ready={readiness?.handbook?.ready}
          blockers={handbookBlockers}
          renderedUrl={agreements.handbook?.rendered_file_url}
          renderIssue={agreements.handbook?.render_issue}
          contentGaps={contentGaps}
        />
      </div>

      {(contractBlockers.length > 0 || handbookBlockers.length > 0) && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-100 rounded-lg">
          <p className="text-sm font-medium text-amber-900 mb-2">What to fix before preparing:</p>
          <ul className="space-y-1.5 text-sm text-amber-800">
            {[...contractBlockers, ...handbookBlockers].map((item) => (
              <li key={`${item.field}-${item.label}`} className="flex items-start gap-2">
                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                <span>
                  <strong>{item.label}</strong>
                  {item.fix_hint ? ` — ${item.fix_hint}` : ''}
                </span>
              </li>
            ))}
          </ul>
          <a
            href="/portal/settings"
            className="inline-flex items-center gap-1.5 mt-3 text-xs font-medium text-amber-900 hover:underline"
          >
            <Settings className="h-3.5 w-3.5" />
            Open organisation settings
          </a>
        </div>
      )}

      {contentGaps.length > 0 && isReady && (
        <p className="mb-4 text-xs text-slate-500">
          {contentGaps.length} handbook content field(s) will use safe default wording.
          Customise them in Settings for organisation-specific text.
        </p>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {!isAuditor && (
          <Button
            onClick={handlePrepare}
            disabled={preparing || !isReady || !!setupGate}
            className="bg-primary hover:bg-primary/90"
            data-testid="prepare-employment-pack-btn"
          >
            {preparing ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Preparing…
              </>
            ) : (
              'Prepare contract & handbook'
            )}
          </Button>
        )}
        <Button variant="outline" size="sm" onClick={fetchReadiness} disabled={loading || preparing}>
          Refresh status
        </Button>
      </div>
    </div>
  );
}

function PackStatusCard({ title, ready, blockers, renderedUrl, renderIssue, contentGaps = [] }) {
  return (
    <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-semibold text-slate-800">{title}</h4>
        {ready ? (
          <CheckCircle className="h-4 w-4 text-green-600" />
        ) : (
          <AlertTriangle className="h-4 w-4 text-amber-600" />
        )}
      </div>
      <p className="text-xs text-slate-600 mb-2">
        {ready
          ? renderedUrl
            ? 'PDF prepared and ready for worker review.'
            : 'Fields complete — click Prepare to generate the PDF.'
          : `${blockers.length} required field(s) missing.`}
      </p>
      {renderIssue && (
        <p className="text-xs text-red-600 mb-2">Render issue: {renderIssue}</p>
      )}
      {contentGaps.length > 0 && ready && (
        <p className="text-xs text-slate-500">{contentGaps.length} optional content gap(s)</p>
      )}
      {renderedUrl && (
        <a
          href={renderedUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
        >
          View PDF
          <ExternalLink className="h-3 w-3" />
        </a>
      )}
    </div>
  );
}
